import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { cardSx, colors, typography } from '../constants';
import { errorMessage, OUTDATED_IMPORT_SERVER } from '../lib/errorMessage';
import { fmt, pct } from '../lib/format';
import { useDataMode } from '../hooks/useSelection';
import useProfitIqTabs from '../hooks/useProfitIqTabs';
import { useImportSales, usePreviewSales } from '../hooks/useProfitData';

const PLATFORMS = [
  {
    id: 'clover',
    name: 'Clover',
    icon: CreditCardOutlinedIcon,
    blurb: 'POS terminal & online orders',
    steps: [
      'Open the Clover Dashboard on a computer.',
      'Go to Reporting → Orders (or Sales).',
      'Set your date range, then choose Export → CSV.',
      'Select the downloaded CSV below.',
    ],
  },
  {
    id: 'square',
    name: 'Square',
    icon: CropSquareIcon,
    blurb: 'Square POS & online',
    steps: [
      'Sign in to the Square Dashboard.',
      'Go to Reports → Item Sales (or Transactions).',
      'Pick a date range and select Export → CSV.',
      'Select the downloaded CSV below.',
    ],
  },
  {
    id: 'orders',
    name: 'Orders.co',
    icon: ShoppingBagOutlinedIcon,
    blurb: 'Delivery aggregator (DoorDash, Uber Eats…)',
    steps: [
      'Log in to your Orders.co dashboard.',
      'Open Reports → Order History.',
      'Select a date range and Export to CSV.',
      'Select the downloaded CSV below.',
    ],
  },
  {
    id: 'other',
    name: 'Other / Generic CSV',
    icon: DescriptionOutlinedIcon,
    blurb: 'Any POS export with order & item columns',
    steps: [
      'Export your sales as a CSV from your POS.',
      'Make sure it has item names, prices and order info.',
      "We'll auto-detect the columns we recognise.",
      'Select the CSV below.',
    ],
  },
];

/**
 * CSV import wizard: pick a platform, choose a file, review the parse, commit.
 *
 * Imported orders are only read while this browser is in CSV data mode — in
 * online mode the analytics read live orders and ignore these rows entirely.
 * An import therefore states which mode is active and offers the switch once
 * it succeeds, rather than changing the mode on the owner's behalf.
 *
 * Parsing is entirely server-side: the preview uploads the file text, and the
 * token it returns is what commits the same parse, so there is no column
 * mapping step and no chance of the preview disagreeing with the import.
 */
export default function ImportSales() {
  const { goToTab } = useProfitIqTabs();
  const { dataMode, setDataMode } = useDataMode();
  const isOnline = dataMode === 'online';

  const [step, setStep] = useState('platform');
  const [platform, setPlatform] = useState(null);
  const [csv, setCsv] = useState('');
  const [fileName, setFileName] = useState('');
  const [mode, setMode] = useState('skip');
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  const previewSales = usePreviewSales();
  const importSales = useImportSales();

  const goDashboard = () => goToTab('overview');

  function reset(full) {
    setCsv('');
    setFileName('');
    setPreview(null);
    setError(null);
    setMode('skip');
    if (full) {
      setPlatform(null);
      setStep('platform');
    } else {
      setStep('data');
    }
  }

  function goBack() {
    if (step === 'platform') goDashboard();
    else if (step === 'preview') setStep('data');
    else reset(true);
  }

  function readFile(file) {
    setError(null);
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => setCsv(String(e.target?.result ?? ''));
    reader.onerror = () => {
      setFileName('');
      setError('Could not read that file. Pick the .csv file from your export.');
    };
    reader.readAsText(file);
  }

  async function runPreview() {
    if (!csv.trim() || !platform) return;
    setError(null);
    try {
      const p = await previewSales.mutateAsync({ csv, source: platform.id });
      if (!p.token) {
        setError(OUTDATED_IMPORT_SERVER);
        return;
      }
      setPreview(p);
      setMode('skip');
      setStep('preview');
    } catch (e) {
      setError(errorMessage(e) || 'Could not read this file.');
    }
  }

  async function confirmImport() {
    if (!preview || !platform) return;
    setError(null);
    try {
      // The preview already uploaded and parsed the file; its token is all the
      // server needs to commit it.
      const res = await importSales.mutateAsync({
        token: preview.token,
        fileName: fileName || `${platform.id}.csv`,
        mode,
      });
      setLastResult({
        imported: res.imported,
        skipped: res.skipped,
        overwritten: res.overwritten,
      });
      setStep('done');
    } catch (e) {
      setError(errorMessage(e) || 'Import failed.');
    }
  }

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', pb: 6, display: 'grid', gap: 1.75 }}>
      <ButtonBase
        onClick={goBack}
        sx={{
          justifySelf: 'start',
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          borderRadius: 1,
          px: 0.5,
          py: 0.25,
        }}
      >
        <ArrowBackIcon sx={{ fontSize: 18, color: colors.foreground }} />
        <Typography
          sx={{
            fontSize: typography.small,
            fontWeight: 600,
            color: colors.foreground,
          }}
        >
          {step === 'platform' ? 'Back to dashboard' : 'Back'}
        </Typography>
      </ButtonBase>

      {error ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            backgroundColor: colors.destructiveSurface,
            borderRadius: 2.5,
            p: 1.5,
          }}
        >
          <WarningAmberIcon
            sx={{ fontSize: 18, color: colors.destructiveSurfaceForeground }}
          />
          <Typography
            sx={{
              flex: 1,
              fontSize: typography.caption,
              color: colors.destructiveSurfaceForeground,
            }}
          >
            {error}
          </Typography>
        </Box>
      ) : null}

      {isOnline && step !== 'done' ? <OnlineModeNotice /> : null}

      {step === 'platform' ? (
        <PlatformPicker
          onPick={(p) => {
            setPlatform(p);
            setStep('data');
          }}
        />
      ) : null}

      {step === 'data' && platform ? (
        <DataStep
          platform={platform}
          csv={csv}
          fileName={fileName}
          onFile={readFile}
          onPreview={runPreview}
          loading={previewSales.isPending}
        />
      ) : null}

      {step === 'preview' && preview && platform ? (
        <PreviewStep
          preview={preview}
          mode={mode}
          setMode={setMode}
          onConfirm={confirmImport}
          loading={importSales.isPending}
        />
      ) : null}

      {step === 'done' && lastResult ? (
        <DoneStep
          result={lastResult}
          isOnline={isOnline}
          onSwitchToCsv={() => setDataMode('csv')}
          onAddMore={() => reset(true)}
          onDashboard={goDashboard}
        />
      ) : null}
    </Box>
  );
}

const h1Sx = {
  fontSize: typography.title,
  fontWeight: 800,
  color: colors.foreground,
};

const subSx = {
  fontSize: typography.small,
  color: colors.mutedForeground,
  mt: 0.5,
  lineHeight: 1.45,
};

const noticeBoxSx = {
  backgroundColor: colors.warningSurface,
  border: `1px solid ${colors.warning}`,
  borderRadius: 3,
  p: 1.75,
  display: 'grid',
  gap: 1,
};

const noticeTitleSx = {
  fontSize: typography.small,
  fontWeight: 800,
  color: colors.warningSurfaceForeground,
};

const noticeSubSx = {
  fontSize: typography.caption,
  color: colors.warningSurfaceForeground,
  lineHeight: 1.5,
};

function PrimaryButton({ children, disabled, loading, onClick }) {
  return (
    <Button
      fullWidth
      variant="contained"
      disableElevation
      onClick={onClick}
      disabled={disabled || loading}
      sx={{
        py: 1.6,
        borderRadius: 3,
        textTransform: 'none',
        fontSize: typography.emphasis,
        fontWeight: 700,
        backgroundColor: colors.primary,
        '&:hover': {
          backgroundColor: colors.primary,
          filter: 'brightness(0.95)',
        },
        '&.Mui-disabled': {
          backgroundColor: colors.border,
          color: colors.faint,
        },
      }}
    >
      {loading ? (
        <CircularProgress size={22} sx={{ color: colors.primaryForeground }} />
      ) : (
        children
      )}
    </Button>
  );
}

/**
 * Says up front that an import will not change what the dashboard shows while
 * the business reads live orders, so nobody imports a file expecting the
 * numbers to move.
 */
function OnlineModeNotice() {
  return (
    <Box sx={noticeBoxSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <InfoOutlinedIcon
          sx={{ fontSize: 18, color: colors.warningSurfaceForeground }}
        />
        <Typography sx={noticeTitleSx}>You&apos;re reading live orders</Typography>
      </Box>
      <Typography sx={noticeSubSx}>
        This browser is set to Online mode. The import is saved, but it only
        counts once you switch to CSV mode.
      </Typography>
    </Box>
  );
}

function PlatformIcon({ icon: Icon }) {
  return (
    <Box
      sx={{
        width: 42,
        height: 42,
        borderRadius: 2.5,
        flexShrink: 0,
        backgroundColor: colors.accent,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon sx={{ fontSize: 22, color: colors.primary }} />
    </Box>
  );
}

function PlatformPicker({ onPick }) {
  return (
    <Box sx={{ display: 'grid', gap: 1.75 }}>
      <Box>
        <Typography sx={h1Sx}>Where does your data come from?</Typography>
        <Typography sx={subSx}>
          Pick a platform to import from. You can add more later — imports from
          different platforms combine into one dashboard, and we never
          double-count the same order.
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gap: 1.25 }}>
        {PLATFORMS.map((p) => (
          <ButtonBase
            key={p.id}
            onClick={() => onPick(p)}
            sx={{
              ...cardSx,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1.75,
              textAlign: 'left',
              '&:hover': { backgroundColor: colors.muted },
            }}
          >
            <PlatformIcon icon={p.icon} />
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontSize: typography.body,
                  fontWeight: 700,
                  color: colors.foreground,
                }}
              >
                {p.name}
              </Typography>
              <Typography
                sx={{
                  fontSize: typography.caption,
                  color: colors.mutedForeground,
                }}
              >
                {p.blurb}
              </Typography>
            </Box>
            <ChevronRightIcon sx={{ fontSize: 22, color: colors.faint }} />
          </ButtonBase>
        ))}
      </Box>
    </Box>
  );
}

function DataStep({ platform, csv, fileName, onFile, onPreview, loading }) {
  const inputRef = useRef(null);
  const canPreview = csv.trim().length > 0;

  return (
    <Box sx={{ display: 'grid', gap: 1.75 }}>
      <Box sx={{ ...cardSx, p: 2, display: 'grid', gap: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <PlatformIcon icon={platform.icon} />
          <Typography
            sx={{
              fontSize: typography.body,
              fontWeight: 800,
              color: colors.foreground,
            }}
          >
            Export from {platform.name}
          </Typography>
        </Box>
        {platform.steps.map((stepTxt, i) => (
          <Box
            key={i}
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}
          >
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                flexShrink: 0,
                mt: '1px',
                backgroundColor: colors.accent,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                sx={{
                  fontSize: typography.caption,
                  fontWeight: 800,
                  color: colors.primary,
                }}
              >
                {i + 1}
              </Typography>
            </Box>
            <Typography
              sx={{
                flex: 1,
                fontSize: typography.caption,
                color: colors.mutedForeground,
                lineHeight: 1.6,
              }}
            >
              {stepTxt}
            </Typography>
          </Box>
        ))}
      </Box>

      <ButtonBase
        onClick={() => inputRef.current?.click()}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          py: 3.25,
          borderRadius: 3,
          border: `2px dashed ${colors.border}`,
          backgroundColor: colors.card,
          '&:hover': { borderColor: colors.primary },
        }}
      >
        <CloudUploadOutlinedIcon sx={{ fontSize: 34, color: colors.primary }} />
        <Typography
          sx={{
            fontSize: typography.small,
            fontWeight: 700,
            color: colors.foreground,
          }}
        >
          {fileName || 'Click to select a CSV file'}
        </Typography>
        <Typography sx={{ fontSize: typography.caption, color: colors.faint }}>
          .csv file from your export
        </Typography>
      </ButtonBase>
      {/* Reset on click so re-picking the same file still fires a change. */}
      <input
        ref={inputRef}
        type="file"
        accept=".csv,text/csv,text/plain"
        hidden
        onClick={(e) => {
          e.target.value = '';
        }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onFile(file);
        }}
      />

      <PrimaryButton
        onClick={onPreview}
        disabled={!canPreview}
        loading={loading}
      >
        Preview import
      </PrimaryButton>
    </Box>
  );
}

function PreviewStep({ preview, mode, setMode, onConfirm, loading }) {
  const hasDupes = preview.duplicateOrders > 0;
  const willAdd = mode === 'overwrite' ? preview.totalOrders : preview.newOrders;

  return (
    <Box sx={{ display: 'grid', gap: 1.75 }}>
      <Box>
        <Typography sx={h1Sx}>Review before importing</Typography>
        <Typography sx={subSx}>
          Detected as a {labelFor(preview.source)} export. Here&apos;s what we
          found.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1.25 }}>
        <StatCard
          label="New orders"
          value={String(preview.newOrders)}
          accent={colors.success}
        />
        <StatCard
          label="Duplicates"
          value={String(preview.duplicateOrders)}
          accent={hasDupes ? colors.warning : colors.mutedForeground}
        />
        <StatCard label="In file" value={String(preview.totalOrders)} />
      </Box>

      <Box sx={{ ...cardSx, p: 1.75, display: 'grid', gap: 1 }}>
        <SummaryRow label="Channels" value={String(preview.summary.channels)} />
        <SummaryRow
          label="Items"
          value={String(preview.summary.itemsImported)}
        />
        <SummaryRow
          label="Date range"
          value={`${preview.summary.dateFrom || '—'} → ${
            preview.summary.dateTo || '—'
          }`}
        />
        <SummaryRow
          label="Total days"
          value={String(preview.summary.totalDays)}
        />
        <SummaryRow
          label="Revenue in file"
          value={fmt(preview.summary.totalRevenue ?? 0)}
        />
        {preview.summary.taxRate != null ? (
          <SummaryRow label="Tax rate" value={pct(preview.summary.taxRate)} />
        ) : null}
      </Box>

      {hasDupes ? (
        <Box sx={noticeBoxSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <ErrorOutlineIcon
              sx={{ fontSize: 18, color: colors.warningSurfaceForeground }}
            />
            <Typography sx={noticeTitleSx}>
              {preview.duplicateOrders} order
              {preview.duplicateOrders === 1 ? '' : 's'} already imported
            </Typography>
          </Box>
          <Typography sx={noticeSubSx}>
            These match orders you imported before. Choose what to do:
          </Typography>
          <Box sx={{ display: 'grid', gap: 1, mt: 0.5 }}>
            <ModeOption
              active={mode === 'skip'}
              title="Skip duplicates"
              desc="Keep what you have, add only new orders"
              onClick={() => setMode('skip')}
            />
            <ModeOption
              active={mode === 'overwrite'}
              title="Overwrite"
              desc="Replace existing orders with this file"
              onClick={() => setMode('overwrite')}
            />
          </Box>
        </Box>
      ) : null}

      <PrimaryButton
        onClick={onConfirm}
        disabled={willAdd === 0}
        loading={loading}
      >
        {willAdd === 0
          ? 'Nothing new to import'
          : mode === 'overwrite'
            ? `Import & overwrite ${preview.totalOrders} orders`
            : `Import ${preview.newOrders} new order${
                preview.newOrders === 1 ? '' : 's'
              }`}
      </PrimaryButton>
    </Box>
  );
}

function DoneStep({
  result,
  isOnline,
  onSwitchToCsv,
  onAddMore,
  onDashboard,
}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        justifyItems: 'center',
        pt: 1.5,
      }}
    >
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          backgroundColor: colors.successSurface,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CheckIcon sx={{ fontSize: 36, color: colors.success }} />
      </Box>
      <Typography sx={h1Sx}>Import complete</Typography>

      <Box
        sx={{
          ...cardSx,
          p: 1.75,
          width: '100%',
          display: 'grid',
          gap: 1,
        }}
      >
        <SummaryRow label="Added" value={String(result.imported)} />
        {result.overwritten > 0 ? (
          <SummaryRow label="Overwritten" value={String(result.overwritten)} />
        ) : null}
        {result.skipped > 0 ? (
          <SummaryRow
            label="Skipped (duplicates)"
            value={String(result.skipped)}
          />
        ) : null}
      </Box>

      {isOnline ? (
        <Box sx={{ ...noticeBoxSx, width: '100%' }}>
          <Typography sx={noticeTitleSx}>
            Not showing on your dashboard yet
          </Typography>
          <Typography sx={noticeSubSx}>
            This browser is reading live orders. Switch to CSV mode to see this
            import instead.
          </Typography>
          <PrimaryButton onClick={onSwitchToCsv}>
            Switch to CSV mode
          </PrimaryButton>
        </Box>
      ) : (
        <Typography sx={{ ...subSx, textAlign: 'center' }}>
          Your dashboard, channels and menu are updated. Import another platform
          anytime to combine more data.
        </Typography>
      )}

      <Box sx={{ width: '100%' }}>
        <PrimaryButton onClick={onDashboard}>Go to dashboard</PrimaryButton>
      </Box>
      <Button
        onClick={onAddMore}
        startIcon={<AddIcon />}
        sx={{
          textTransform: 'none',
          fontSize: typography.body,
          fontWeight: 700,
          color: colors.primary,
        }}
      >
        Import more data
      </Button>
    </Box>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <Box sx={{ ...cardSx, flex: 1, py: 1.75, textAlign: 'center' }}>
      <Typography
        sx={{
          fontSize: typography.display,
          fontWeight: 800,
          color: accent ?? colors.foreground,
        }}
      >
        {value}
      </Typography>
      <Typography
        sx={{
          fontSize: typography.caption,
          color: colors.mutedForeground,
          mt: 0.25,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function SummaryRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
      <Typography
        sx={{ fontSize: typography.caption, color: colors.mutedForeground }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: typography.caption,
          fontWeight: 700,
          color: colors.foreground,
          textAlign: 'right',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function ModeOption({ active, title, desc, onClick }) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        p: 1.5,
        borderRadius: 2.5,
        textAlign: 'left',
        backgroundColor: colors.card,
        border: `1.5px solid ${active ? colors.primary : colors.border}`,
      }}
    >
      <Box
        sx={{
          width: 20,
          height: 20,
          borderRadius: '50%',
          flexShrink: 0,
          border: `2px solid ${active ? colors.primary : colors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {active ? (
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: colors.primary,
            }}
          />
        ) : null}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography
          sx={{
            fontSize: typography.small,
            fontWeight: 700,
            color: colors.foreground,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontSize: typography.caption,
            color: colors.mutedForeground,
          }}
        >
          {desc}
        </Typography>
      </Box>
    </ButtonBase>
  );
}

function labelFor(src) {
  return PLATFORMS.find((p) => p.id === src)?.name ?? 'CSV';
}
